import { fromJS } from 'immutable';
import uuid from 'uuid/v4';
import reducer from 'src/utils/redux/reducer';
import {
  PIPELINE_DROP, PIPELINE_SET_APPLY, PIPELINE_TOGGLE_APPLY,
  PIPELINE_TOGGLE_FIELDS_LIST, PIPELINE_TOGGLE_SAVED_LIST,
  PIPELINE_ADD_PIPE, PIPELINE_DELETE_PIPE, PIPELINE_MOVE_PIPE,
  PIPELINE_SET_PIPE_TYPE, PIPELINE_SET_PIPE_LOGIC, PIPELINE_SET_PIPES,
  PIPELINE_ADD_FIELD, PIPELINE_DELETE_FIELD, PIPELINE_MOVE_FIELD,
  PIPELINE_SET_FIELD_IS, PIPELINE_SET_FIELD_TYPE, PIPELINE_SET_FIELD_CLAUSE,
  PIPELINE_SET_FIELD_SORT, PIPELINE_ADD_GROUP, PIPELINE_SET_PROGRESS,
  PIPELINE_SAVE, PIPELINE_SET,
} from './types';
import { filter, fields, pipeline } from 'src/types';

export const defaults = {
  pipe: {
    type: pipeline.TYPE.MATCH,
    logic: filter.LOGIC.AND,
  },
  field: {
    is: filter.IS.IS,
    type: filter.TYPE.CONTAINS,
    dateTimeType: filter.TYPE_DATE.EQUAL_AFTER,
    sort: filter.SORT.ASC,
    clause: null,
  },
};

const getPipe = ({
  type = defaults.pipe.type,
  logic = defaults.pipe.logic,
  fields = [],
} = {}) => fromJS({
  uuid: uuid(),
  type,
  logic,
  fields: fields.map(field => getField(field.code, field.is, field.type, field.params)),
});

const getField = (
  code,
  is = defaults.field.is,
  type,
  {
    sort = defaults.field.sort,
    clause = defaults.field.clause,
  } = {},
) => fromJS({
  uuid: uuid(),
  code,
  is,
  type: type || (fields[code].isDateTime ? defaults.field.dateTimeType : defaults.field.type),
  sort,
  clause,
});

const getGroup = () => fromJS({
  uuid: uuid(),
  logic: filter.LOGIC.AND,
  fields: [],
});

const getProgress = ({ percent = 0, pipe = null } = {}) => ({
  percent,
  pipe,
});

// --- initialState ---

export const initialPipeline = {
  id: null,
  name: '',
  isShared: false,
  isOwn: true,
  ownerId: null,
  applied: false,
  modified: false,
  progress: getProgress(),
  pipes: fromJS([getPipe()]),
};

export const initialState = {
  current: {
    ...initialPipeline,
    fieldsList: true,
    savedList: false,
  },
};

// --- handlers ---

const handleSetInitial = () => initialState;

const handlePipelineDrop = state => ({
  ...state,
  current: {
    ...state.current,
    ...initialPipeline,
  },
});

const handlePipelineSetApply = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    applied: action.applied,
    modified: false,
    progress: getProgress({ doing: action.applied, }),
  },
});

const handlePipelineToggleApply = state=> ({
  ...state,
  current: {
    ...state.current,
    applied: !state.current.applied,
    modified: false,
    progress: getProgress({ doing: !state.current.applied, }),
  },
});

const handlePipelineFieldsList = state => ({
  ...state,
  current: {
    ...state.current,
    fieldsList: !state.current.fieldsList,
  },
});

const handlePipelineToggleSavedList = state => ({
  ...state,
  current: {
    ...state.current,
    savedList: !state.current.savedList,
  },
});

const handlePipelineAddPipe = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.push(getPipe({ ...action, type: action.pipeType })),
  },
});

const handlePipelineDeletePipe = (state, action) => {
  const pipes = state.current.pipes.delete(action.pipeId);

  return {
    ...state,
    current: {
      ...state.current,
      modified: true,
      pipes: pipes.size > 0 ? pipes : initialState.current.pipes,
    },
  };
};

const handlePipelineMovePipe = (state, action) => {
  const pipe = state.current.pipes.get(action.pipeId);
  const pipes = state.current.pipes.delete(action.pipeId);

  return {
    ...state,
    current: {
      ...state.current,
      modified: true,
      pipes: pipes.insert(action.destinationId, pipe),
    },
  };
};

const handlePipelineSetPipes = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    pipes: fromJS(action.pipes),
  },
});

const handlePipelineAddField = (state, action) => {
  const pipeId = action.pipeId >= 0 ? action.pipeId: state.current.pipes.size - 1;
  const is = action.is;
  const type = action.compareType;
  let fields = state.current.pipes.getIn([pipeId, 'fields']);

  fields = action.index >= 0
    ? fields.insert(action.index, getField(action.field, is, type, action.params))
    : fields.push(getField(action.field, is, type, action.params));

  return {
    ...state,
    current: {
      ...state.current,
      modified: true,
      pipes: state.current.pipes.setIn([pipeId, 'fields'], fields),
    },
  };
};

const handlePipelineDeleteField = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.deleteIn([action.pipeId, 'fields', action.fieldId]),
  },
});

const handlePipelineMoveField = (state, action) => {
  const field = state.current.pipes.getIn([action.sourcePipeId, 'fields', action.fieldId]);
  const pipes = state.current.pipes.deleteIn([action.sourcePipeId, 'fields', action.fieldId]);
  const fields = pipes.getIn([action.destinationPipeId, 'fields']);

  return {
    ...state,
    current: {
      ...state.current,
      modified: true,
      pipes: pipes.setIn([action.destinationPipeId, 'fields'], fields.insert(action.destinationId, field)),
    },
  };
};

const handlePipelineSetPipeType = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'type'], action.pipeType),
  },
});

const handlePipelineSetPipeLogic = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'logic'], action.pipeLogic),
  },
});

const handlePipelineSetFieldIs = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'fields', action.fieldId, 'is'], action.is),
  },
});

const handlePipelineSetFieldType = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'fields', action.fieldId, 'type'], action.fieldType),
  },
});

const handlePipelineSetFieldClause = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'fields', action.fieldId, 'clause'], action.clause),
  },
});

const handlePipelineSetFieldSort = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'fields', action.fieldId, 'sort'], action.sort),
  },
});

const handlePipelineAddGroup = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    modified: true,
    pipes: state.current.pipes.setIn([action.pipeId, 'fields'], getGroup()),
  },
});

const handlePipelineSetProgress = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    progress: getProgress({ percent: action.percent, pipe: action.pipe || state.current.progress.pipe }),
  },
});

const handlePipelineSet = (state, action) => ({
  ...state,
  current: {
    ...state.current,
    ...action.payload,
    pipes: action.payload.pipes ? fromJS(action.payload.pipes) : state.current.pipes,
  },
});

const handlers = {
  'SET_INITIAL': handleSetInitial,
  [PIPELINE_DROP]: handlePipelineDrop,
  [PIPELINE_SET_APPLY]: handlePipelineSetApply,
  [PIPELINE_TOGGLE_APPLY]: handlePipelineToggleApply,
  [PIPELINE_TOGGLE_FIELDS_LIST]: handlePipelineFieldsList,
  [PIPELINE_TOGGLE_SAVED_LIST]: handlePipelineToggleSavedList,
  [PIPELINE_ADD_PIPE]: handlePipelineAddPipe,
  [PIPELINE_DELETE_PIPE]: handlePipelineDeletePipe,
  [PIPELINE_MOVE_PIPE]: handlePipelineMovePipe,
  [PIPELINE_SET_PIPES]: handlePipelineSetPipes,
  [PIPELINE_ADD_FIELD]: handlePipelineAddField,
  [PIPELINE_DELETE_FIELD]: handlePipelineDeleteField,
  [PIPELINE_MOVE_FIELD]: handlePipelineMoveField,
  [PIPELINE_SET_PIPE_TYPE]: handlePipelineSetPipeType,
  [PIPELINE_SET_PIPE_LOGIC]: handlePipelineSetPipeLogic,
  [PIPELINE_SET_FIELD_IS]: handlePipelineSetFieldIs,
  [PIPELINE_SET_FIELD_TYPE]: handlePipelineSetFieldType,
  [PIPELINE_SET_FIELD_CLAUSE]: handlePipelineSetFieldClause,
  [PIPELINE_SET_FIELD_SORT]: handlePipelineSetFieldSort,
  [PIPELINE_ADD_GROUP]: handlePipelineAddGroup,
  [PIPELINE_SET_PROGRESS]: handlePipelineSetProgress,
  [PIPELINE_SET]: handlePipelineSet,
};

// --- reducerFactory ---

export default flows => reducer({ initialState, handlers, flows });